/**
 * Módulo: Calculator - Funções Puras para Cálculo de Estoque
 * Arquivo: js/pages/suprimentos/estoque/calculator.js
 * 
 * REGRAS DE NEGÓCIO:
 * 1. Custo Médio Ponderado com RESET ao zerar estoque
 * 2. Sem efeitos colaterais (pure functions)
 * 3. Testável isoladamente
 */

window.FazendaRP = window.FazendaRP || {};
window.FazendaRP.Estoque = window.FazendaRP.Estoque || {};
window.FazendaRP.Estoque.Calculator = {

    /**
     * Calcula novo custo médio ponderado após entrada
     * 
     * REGRA: Se estoque zerado, custo médio ZERA e reinicia com custo da entrada
     * 
     * @param {number} saldoAnterior - Saldo antes da entrada
     * @param {number} custoAnterior - Custo médio antes da entrada  
     * @param {number} quantidadeEntrada - Quantidade da nova entrada
     * @param {number} custoEntrada - Custo unitário da entrada
     * @returns {number} Novo custo médio
     * 
     * @example
     * // Estoque zerado: reinicia custo
     * calcularCustoMedio(0, 10, 100, 5) // => 5 (ignora custo anterior)
     * 
     * @example  
     * // Estoque existente: média ponderada
     * calcularCustoMedio(100, 5, 50, 6) // => (100*5 + 50*6)/150 = 5.33
     */
    calcularCustoMedio: function (saldoAnterior, custoAnterior, quantidadeEntrada, custoEntrada) {
        // Validações básicas
        if (quantidadeEntrada < 0) {
            throw new Error('Quantidade de entrada deve ser positiva');
        }
        if (custoEntrada < 0) {
            throw new Error('Custo de entrada deve ser positivo ou zero');
        }

        // REGRA 1: Se saldo zerado, custo médio ZERA e REINICIA
        if (saldoAnterior <= 0) {
            console.log(`[Calculator] RESET - Saldo zerado. Novo custo: ${custoEntrada}`);
            return custoEntrada;
        }

        // REGRA 2: Média ponderada
        const valorAnterior = saldoAnterior * custoAnterior;
        const valorEntrada = quantidadeEntrada * custoEntrada;
        const novoSaldo = saldoAnterior + quantidadeEntrada;

        // Evitar divisão por zero (edge case improvável)
        if (novoSaldo === 0) {
            console.warn('[Calculator] Saldo ficou zero após entrada (improvável)');
            return custoAnterior;
        }

        const novoCusto = (valorAnterior + valorEntrada) / novoSaldo;

        console.log(`[Calculator] Média Ponderada: (${saldoAnterior}×${custoAnterior} + ${quantidadeEntrada}×${custoEntrada}) / ${novoSaldo} = ${novoCusto.toFixed(2)}`);

        return novoCusto;
    },

    /**
     * Calcula novo saldo após movimentação
     * 
     * @param {number} saldoAtual - Saldo atual
     * @param {string} tipo - 'ENTRADA' ou 'SAIDA'
     * @param {number} quantidade - Quantidade movimentada (sempre positiva)
     * @returns {number} Novo saldo
     * 
     * @example
     * calcularNovoSaldo(100, 'ENTRADA', 50) // => 150
     * calcularNovoSaldo(100, 'SAIDA', 30)   // => 70
     */
    calcularNovoSaldo: function (saldoAtual, tipo, quantidade) {
        if (quantidade < 0) {
            throw new Error('Quantidade deve ser positiva');
        }

        if (tipo === 'ENTRADA') {
            return saldoAtual + quantidade;
        } else if (tipo === 'SAIDA') {
            return saldoAtual - quantidade;
        } else {
            throw new Error(`Tipo inválido: ${tipo}. Use 'ENTRADA' ou 'SAIDA'`);
        }
    },

    /**
     * Valida movimentação antes de processar
     * 
     * @param {Object} movimentacao - Objeto da movimentação
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    validarMovimentacao: function (movimentacao) {
        const errors = [];

        // Campos obrigatórios
        if (!movimentacao.itemNome) errors.push('itemNome é obrigatório');
        if (!movimentacao.tipo) errors.push('tipo é obrigatório');
        if (movimentacao.quantidade === undefined || movimentacao.quantidade === null) {
            errors.push('quantidade é obrigatória');
        }

        // Validar tipo
        if (movimentacao.tipo && !['ENTRADA', 'SAIDA'].includes(movimentacao.tipo)) {
            errors.push('tipo deve ser ENTRADA ou SAIDA');
        }

        // Validar quantidade
        if (movimentacao.quantidade !== undefined && movimentacao.quantidade <= 0) {
            errors.push('quantidade deve ser maior que zero');
        }

        // Validar custo unitário (apenas para entradas)
        if (movimentacao.tipo === 'ENTRADA' && movimentacao.custoUnitario !== undefined && movimentacao.custoUnitario < 0) {
            errors.push('custoUnitario não pode ser negativo');
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }
};

// TESTES UNITÁRIOS (executar no console)
if (typeof module !== 'undefined' && module.exports) {
    // Node.js environment (para testes)
    module.exports = window.FazendaRP.Estoque.Calculator;
}

// Auto-teste ao carregar (apenas em DEV)
if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
    console.log('[Calculator] Executando auto-testes...');

    const calc = window.FazendaRP.Estoque.Calculator;

    try {
        // Teste 1: RESET quando saldo zera
        const t1 = calc.calcularCustoMedio(0, 10, 100, 5);
        console.assert(t1 === 5, `Teste 1 falhou: esperado 5, obtido ${t1}`);

        // Teste 2: Média ponderada
        const t2 = calc.calcularCustoMedio(100, 5, 50, 6);
        const esperado = (100 * 5 + 50 * 6) / 150; // 5.33...
        console.assert(Math.abs(t2 - esperado) < 0.01, `Teste 2 falhou: esperado ${esperado}, obtido ${t2}`);

        // Teste 3: Novo saldo ENTRADA
        const t3 = calc.calcularNovoSaldo(100, 'ENTRADA', 50);
        console.assert(t3 === 150, `Teste 3 falhou: esperado 150, obtido ${t3}`);

        // Teste 4: Novo saldo SAIDA
        const t4 = calc.calcularNovoSaldo(100, 'SAIDA', 30);
        console.assert(t4 === 70, `Teste 4 falhou: esperado 70, obtido ${t4}`);

        // Teste 5: Validação
        const t5 = calc.validarMovimentacao({ itemNome: 'Teste', tipo: 'ENTRADA', quantidade: 10 });
        console.assert(t5.valid === true, `Teste 5 falhou: validação deveria passar`);

        const t6 = calc.validarMovimentacao({ itemNome: '', tipo: 'INVALIDO', quantidade: -5 });
        console.assert(t6.valid === false, `Teste 6 falhou: validação deveria falhar`);
        console.assert(t6.errors.length > 0, `Teste 6 falhou: deveria ter erros`);

        console.log('✅ Todos os testes do Calculator passaram!');
    } catch (e) {
        console.error('❌ Falha nos testes do Calculator:', e);
    }
}
